import { useEffect, useState } from 'react';
import { useQuery } from '@hotjar/webapp/utils/routing/utils/get-route';
import { useNavigate } from 'react-router-dom';
import { parseWithCustomParserAndDefaultValue } from '@hotjar/webapp/features/Highlights/List/utils/query-filter-validators';
import { useUpdateEffect } from 'react-use';

export const DEFAULT_CURRENT_PAGE = 1;

export const DEFAULT_ITEMS_PER_PAGE = 30;

const PaginationFilters = {
  PAGE: 'page',
  ITEMS_PER_PAGE: 'items_per_page',
};

type Pagination = { page: number; itemsPerPage: number };

const initialPagination: { page: number; itemsPerPage: number } = {
  page: DEFAULT_CURRENT_PAGE,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
};

interface UseHighlightsPaginationProps {
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;
}

export const useHighlightsPagination = <TCollectionId>(
  collectionId: TCollectionId,
): UseHighlightsPaginationProps => {
  const navigate = useNavigate();
  const queryParams = useQuery();

  const getInitialQuery = (): Pagination => {
    const pagination = initialPagination;

    pagination.page = parseWithCustomParserAndDefaultValue(
      queryParams.get(PaginationFilters.PAGE),
      DEFAULT_CURRENT_PAGE,
      (value: unknown): number => {
        if (typeof value !== 'number') {
          throw new Error('Invalid author filter parameter');
        }

        return value || DEFAULT_CURRENT_PAGE;
      },
    );

    pagination.itemsPerPage = parseWithCustomParserAndDefaultValue(
      queryParams.get(PaginationFilters.ITEMS_PER_PAGE),
      DEFAULT_ITEMS_PER_PAGE,
      (value: unknown): number => {
        if (typeof value !== 'number') {
          throw new Error('Invalid author filter parameter');
        }

        return value;
      },
    );

    return pagination;
  };

  const query = getInitialQuery();

  const [currentPage, setCurrentPage] = useState(query.page);
  const [itemsPerPage, setItemsPerPage] = useState(query.itemsPerPage);

  useUpdateEffect(() => {
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, [collectionId]);

  useEffect(() => {
    queryParams.set(PaginationFilters.PAGE, JSON.stringify(currentPage));
    queryParams.set(PaginationFilters.ITEMS_PER_PAGE, JSON.stringify(itemsPerPage));
    navigate(`.?${new URLSearchParams(queryParams).toString()}`, { replace: true });
  }, [currentPage, itemsPerPage, navigate, queryParams]);

  return {
    currentPage,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
  };
};
